import classNames from "classnames";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router";
import { SaveIcon, TrashIcon, ViewIcon } from "../../../../../../assets/icons";
import { Legislation } from "../../../../../models";
import {
  getLoggedUser,
  isViewedItem,
  saveViewedItem,
} from "../../../../../utils";
import {
  formatDate,
  getText,
  sanitizeHtml,
} from "../../../../../utils/_generic";
import {
  getHighlightedExcerpts,
  highlightFullText,
} from "../../../../../utils/_getHighlightedExcerpts";
import { saveDocumentWithNotify } from "../../../../../utils/_handleSaveDocument";
import PDLTypography from "../../../dna/_pdltypography";
import { ActionButton } from "../../../global/_actionButton";
import ArticleItem from "./_articleItem";

export interface LegislationItemProps {
  item: Legislation;
  index: number;
  searchTerm?: string;
  pushDown?: boolean;
  cardProfileStyle?: boolean;
  onRemove?: (item: Legislation) => void;
}

const LegislationItem = ({
  item,
  index,
  searchTerm,
  pushDown = false,
  cardProfileStyle = false,
  onRemove,
}: LegislationItemProps) => {
  const dispatch = useDispatch();

  const [isSavingDocument, setIsSavingDocument] = useState(false);
  const itemId = item._id || item.id || `generic-${index}`;
  const sanitizedHtml = sanitizeHtml(item.ementa || "");
  const textOnly = getText(sanitizedHtml);

  const loggedUser = useSelector(getLoggedUser);

  const isIpAccount = loggedUser.origem === "ip_auth";

  const navigate = useNavigate();

  const excerpt = searchTerm
    ? getHighlightedExcerpts(textOnly, searchTerm, 150)
    : null;

  const opened = isViewedItem(itemId) && !cardProfileStyle;

  const fullTitle = item?.apelidos
    ?.filter((text) => text.trim().length > 1)
    .slice(0, 2)
    .join(" - ");

  const highlightedTitle = highlightFullText(fullTitle || "", searchTerm);

  const displayTitle = highlightedTitle ? highlightedTitle : fullTitle || "";

  const cleanTitle = sanitizeHtml(displayTitle);

  const handleSaveDocument = () =>
    saveDocumentWithNotify({
      dispatch,
      setIsSavingDocument,
      documentoId: item?._id ?? "",
      id: item?.id,
      tipo: "Legislacao",
      url: window.location.href,
    });

  const renderActions = (className: string) => {
    return (
      <div className={classNames("btn-action-container flex gap-2", className)}>
        {!isIpAccount && (
          <ActionButton
            icon={<SaveIcon />}
            onClick={handleSaveDocument}
            disabled={isSavingDocument}
            label={isSavingDocument ? "Salvando..." : "Salvar"}
          />
        )}

        <ActionButton
          icon={<ViewIcon />}
          label="Ver norma completa"
          onClick={() => navigate(`/legislacao/${item.id}`)}
        />
      </div>
    );
  };

  return (
    <div
      key={itemId}
      id={`result-item-${itemId}`}
      className={`pdl_components_lista_resultados_item mt-3 ${
        opened ? "opened" : ""
      } ${pushDown ? "push-down" : ""} ${cardProfileStyle ? "card-style" : ""}`}
    >
      {renderActions("mobile")}
      <header className="title-wrapper">
        <Link
          to={`/legislacao/${item.id}`}
          className="text-decoration-none"
          onClick={() => {
            saveViewedItem(itemId);
            sessionStorage.setItem("last-clicked-result-item", itemId);
          }}
        >
          <PDLTypography
            size="md"
            weight="semibold"
            variant="brand"
            upper
            className="title-link"
          >
            <span
              className="pdl-title-clamp-3 item-title"
              dangerouslySetInnerHTML={{
                __html: `${
                  opened ? `<span class="opened-status">Acessado</span>` : ""
                } ${cleanTitle}`,
              }}
            />
          </PDLTypography>
        </Link>
      </header>

      <div className="result-meta mb-2 mt-2 flex justify-between items-start">
        <div className="flex">
          <PDLTypography as="span" size="sm">
            Legislação
          </PDLTypography>
          {item.tipo_doc &&
            item.tipo_doc.map((type) => (
              <>
                &nbsp;&bull;&nbsp;
                <PDLTypography as="span" size="sm">
                  {type}
                </PDLTypography>
              </>
            ))}
          {item.data && (
            <>
              &nbsp;&bull;&nbsp;
              <PDLTypography as="span" size="sm">
                {formatDate(item.data)}
              </PDLTypography>
            </>
          )}
        </div>

        {renderActions("desktop")}
      </div>

      <div className="text-container">
        {!!excerpt && (
          <PDLTypography size="sm" weight="medium" className="mb-1">
            Texto encontrado em:
          </PDLTypography>
        )}

        <div
          className="content text-sm text-gray-800"
          dangerouslySetInnerHTML={{
            __html: excerpt || textOnly.slice(0, 500),
          }}
        />
      </div>
      {item.artigos_relevantes && item.artigos_relevantes.length > 0 && (
        <div className="article-container">
          {item.artigos_relevantes.slice(0, 3).map((art, i) => (
            <ArticleItem
              key={art.ref}
              item={art}
              index={i}
              legislationId={item.id}
              searchTerm={searchTerm || ""}
            />
          ))}
        </div>
      )}

      {pushDown && (
        <div className="btn-action-container-footer">
          <div className="btn-action-group-left">
            <ActionButton
              icon={<ViewIcon />}
              label="Ver norma completa"
              onClick={() => navigate(`/legislacao/${item.id}`)}
            />
          </div>
          {onRemove && (
            <div className="btn-action-group-right">
              <button
                type="button"
                className="btn-remove-saved"
                onClick={() => onRemove(item)}
              >
                <TrashIcon color="#8c0000" />
                <span>Remover de salvos</span>
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LegislationItem;
